import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, message, Spin } from 'antd';
import dayjs from 'dayjs';
import type { PolicyDetailsDTO, PolicyExtractionDTO } from '../../../infrastructure/models';
import { getPolicyDetails } from '../../../infrastructure/apiClient';
import styles from './PolicyDetails.module.scss';
import { sortPolicyResults } from '../utils/sortPolicyResults';
import { groupPolicyResultsByType } from '../utils/groupPolicyResultsByType';
import { openPolicyFailureReportMail } from '../utils/reportUtils';

interface PolicyDetailsProps {
  policy?: PolicyExtractionDTO | null;
  onBack: () => void;
  tenantId?: string;
}

const PolicyDetails = ({ policy, onBack, tenantId }: PolicyDetailsProps) => {
  const [details, setDetails] = useState<PolicyDetailsDTO | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!policy?.tenantId || !policy?.id) {
      setDetails(null);
      return;
    }

    const controller = new AbortController();

    const loadDetails = async () => {
      try {
        setLoading(true);
        setDetails(null);
        const tenantToUse = tenantId ?? policy.tenantId;
        const response = await getPolicyDetails(tenantToUse, policy.id, controller.signal);
        const sortedResults = sortPolicyResults(response?.results);
        setDetails({ ...response, results: sortedResults });
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('Failed to load policy details', err);
          message.error('Unable to load policy details.');
          setDetails(null);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadDetails();

    return () => controller.abort();
  }, [policy?.tenantId, policy?.id, tenantId]);

  const source = useMemo(() => details ?? policy ?? null, [details, policy]);
  const hasResults = (details?.results?.length ?? 0) > 0;
  const hasFiles = (details?.files?.length ?? 0) > 0;
  const hasErrors = (details?.errors?.length ?? 0) > 0;

  const groupedResults = useMemo(() => groupPolicyResultsByType(details?.results ?? []), [details?.results]);

  const handleReportIssue = useCallback(() => {
    if (!source) {
      message.warning('Select a policy before reporting.');
      return;
    }

    openPolicyFailureReportMail({
      source,
      details,
      policy: policy ?? null,
      tenantId,
    });
  }, [details, policy, source, tenantId]);

  const meta = useMemo(() => {
    if (!source) {
      return [];
    }

    return [
      { label: 'Location', value: source.location },
      { label: 'Status', value: source.status },
      { label: 'Version', value: source.version?.toString() ?? 'N/A' },
      { label: 'Created', value: dayjs(source.createdAt).format('MMM D, YYYY h:mm A') },
      { label: 'Updated', value: dayjs(source.updatedAt).format('MMM D, YYYY h:mm A') },
      { label: 'Created By', value: source.createdBy },
    ];
  }, [source]);

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <h1 className={styles.title}>Policy Details</h1>
        <Button onClick={onBack} type="default">
          Back
        </Button>
      </header>

      <section className={styles.content}>
        {loading && (
          <div className={styles.loadingState}>
           <Spin size="large" />
          </div>
        )}

        {!loading && (
          <>
            <p className={styles.summary}>
              {!source ? (
                'Select a policy to view its details.'
              ) : hasResults ? (
                <>
                  Extraction details for policy of <strong>{source.location}</strong>.
                </>
              ) : hasErrors ? (
                <>
                  No extraction results available for <strong>{source.location}</strong>. Review the reported issues below.
                </>
              ) : (
                <>
                  Extraction results are still processing for <strong>{source.location}</strong>.
                </>
              )}
            </p>

            {meta.length > 0 && (
              <div className={styles.meta}>
                {meta.map((item) => (
                  <div key={item.label} className={styles.metaItem}>
                    <span className={styles.metaLabel}>{item.label}</span>
                    <span className={styles.metaValue}>{item.value}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {!loading && source && hasFiles && details && (
          <div className={styles.filesSection}>
            <h2 className={styles.sectionTitle}>Source Files</h2>
            <div className={styles.filesList}>
              {details.files.map((file) => (
                <span key={file} className={styles.fileChip}>
                  {file}
                </span>
              ))}
            </div>
          </div>
        )}

        {!loading && source && hasResults && details && (
          <div className={styles.results}>
            {groupedResults.map(({ type, results }) => (
              <section key={type} className={styles.resultGroup}>
                <header className={styles.resultGroupHeader}>
                  <h2 className={styles.resultType}>{type}</h2>
                </header>
                <div className={styles.resultItems}>
                  {results.map((result) => (
                    <article key={result.id} className={styles.resultItem}>
                      <h3 className={styles.resultName}>{result.name}</h3>
                      <p className={styles.resultContent}>{result.content}</p>
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {!loading && source && !hasResults && hasErrors && details && (
          <>
            <div className={styles.errorBox} role="alert">
              <h2 className={styles.sectionTitle}>Extraction Issues</h2>
              <ul className={styles.errorList}>
                {details.errors.map((err) => (
                  <li key={err}>{err}</li>
                ))}
              </ul>
            </div>
            <div className={styles.reportAction}>
              <Button type="primary" onClick={handleReportIssue}>
                Report to Admin
              </Button>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default PolicyDetails;
